import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Tag } from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsPage() {
  const { theme } = useTheme();
  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const fetchNews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (err) {
      // Error fetching news - fail silently
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchNews();
  }, [fetchNews]);

  const categories = ['All', ...new Set(news.map(article => article.category))];
  
  const filteredNews = selectedCategory === 'All' 
    ? news 
    : news.filter(article => article.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div data-testid="news-page" className={`min-h-screen pt-24 flex items-center justify-center ${
        theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'
      }`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00C8EB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Loading news...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="news-page" className={`min-h-screen pt-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'}`}>
      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <span className="text-[#00C8EB] font-medium uppercase tracking-wider text-sm">
            News & Updates
          </span>
          <h1 className={`text-4xl md:text-5xl font-bold mt-3 mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Latest from{' '}
            <span className="text-gradient">ATLAS SOFTECH</span>
          </h1>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Stay updated with the latest technology trends, company announcements, and industry insights.
          </p>
        </motion.div>
      </section>

      {/* Category Filter */}
      {categories.length > 1 && (
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                data-testid={`category-${category.toLowerCase()}`}
                className={selectedCategory === category 
                  ? 'bg-[#00C8EB] text-[#0A0A10]' 
                  : theme === 'dark' 
                    ? 'border-[#464B8B]/30 text-gray-300' 
                    : ''
                }
              >
                {category}
              </Button>
            ))}
          </div>
        </section>
      )}

      {/* News Grid */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-24">
        {filteredNews.length === 0 ? (
          <div className="text-center py-20">
            <p className={`text-xl ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No news articles available yet.
            </p>
            <p className={`mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
              Check back soon for updates!
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`news-card-${article.id}`}
                className={`group overflow-hidden rounded-2xl ${
                  theme === 'dark'
                    ? 'bg-[#12131F] border border-[#464B8B]/20'
                    : 'bg-white border border-gray-200 shadow-sm'
                }`}
              >
                {/* Image */}
                {article.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#00C8EB] bg-[#00C8EB]/10 px-2 py-1 rounded-full">
                      <Tag size={12} weight="duotone" />
                      {article.category}
                    </span>
                    <span className={`flex items-center gap-1 text-xs ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      <Calendar size={12} weight="duotone" />
                      {formatDate(article.created_at)}
                    </span>
                  </div>

                  <h2 className={`text-xl font-bold mb-2 line-clamp-2 group-hover:text-[#00C8EB] transition-colors ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {article.title}
                  </h2>

                  <p className={`mb-4 line-clamp-3 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {article.summary}
                  </p>

                  <Link 
                    to={`/news/${article.id}`}
                    className="inline-flex items-center gap-2 text-[#00C8EB] font-medium group-hover:gap-3 transition-all"
                  >
                    Read More <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
